/**
 * I client delle API, disponibili a ogni pagina.
 *
 * Con un router le pagine non ricevono più i client come proprietà da un
 * componente radice: sarebbero dieci livelli di passaggio per un oggetto che
 * non cambia mai. Li prendono da qui.
 *
 * Due provider, non uno: `SessionApiProvider` costruisce i client dal client
 * HTTP della sessione ed è quello che monta l'applicazione; `ApiProvider`
 * accetta client già fatti ed è quello che usano i test, che non hanno una
 * sessione da cui partire.
 *
 * I client si costruiscono **una volta sola**: ricostruirli a ogni render
 * farebbe ripartire ogni lettura, perché gli hook dei dati dipendono dalla loro
 * identità.
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';

import { useAuth } from '../auth/AuthContext';
import { AccountApi } from './account';
import { AgentsApi } from './agents';
import { BillingApi } from './billing';
import { PriceListApi } from './priceList';
import { DirectoryApi } from './directory';
import { DeadlinesApi } from './deadlines';
import { GiftCodesApi } from './giftCodes';
import { MobileReleaseApi } from './mobileRelease';
import { DemoLinksApi } from './hosting/demoLinks';
import { HostingEnvironmentsApi } from './hosting/environments';
import { SiteEgressApi } from './hosting/siteEgress';
import { HostingStorageApi } from './hosting/storage';
import { PermissionsApi } from './permissions';
import { AlertsApi } from './platform/alerts';
import { ReleaseReposApi } from './platform/releaseRepos';
import { SystemAgentApi } from './platform/systemAgent';
import { TemplatesApi } from './platform/templates';
import { ProjectProfileApi } from './projectProfile';
import { ProjectsApi } from './projects';
import { RequirementsApi } from './requirements/requirements';
import { SecretsApi } from './secrets';
import { SpecialistsApi } from './specialists';
import { SupportApi } from './support';
import { TasksApi } from './tasks/tasksApi';
import { TeamApi } from './team';
import { ToolsApi } from './tools';
import { VoiceApi } from './voice';
import { WorkApi } from './work';

/** I client esposti alle pagine. */
export interface ApiBundle {
  /** Progetti, dipendenze dichiarate e permessi. */
  projects: ProjectsApi;
  /** Set di agenti, agenti, progetti del set e conversazione. */
  agents: AgentsApi;
  /** Credenziali dei set: si scrivono, non si rileggono. */
  secrets: SecretsApi;
  /** Connessioni agli strumenti esterni e loro collegamenti ai progetti. */
  tools: ToolsApi;
  /** Template di sito pubblicati in anteprima. */
  templates: TemplatesApi;
  /** Allarmi del cluster inoltrati su Telegram (solo amministratori). */
  alerts: AlertsApi;
  /** Agente «voce» del set: sessioni, minuti, storico (ADR 0028). */
  voice: VoiceApi;
  /** Abbonamento, ricariche e pagamenti con Stripe (ADR 0027). */
  billing: BillingApi;
  /** Listino modificabile dall'amministratore (ADR 0058). */
  priceList: PriceListApi;
  /** Task manager del set: task, proposte, cronologia, commenti (ADR 0029). */
  tasks: TasksApi;
  /** Sessioni, password, registro accessi e telefoni del profilo (ADR 0030). */
  account: AccountApi;
  /** Ricerca dei set e conteggi «da fare» per il guscio (ADR 0030). */
  directory: DirectoryApi;
  /** Membri e inviti dei set (ADR 0032). */
  team: TeamApi;
  /** Task di tutti i set, scadenze, siti, consumo per progetto (ADR 0032). */
  work: WorkApi;
  /** Richieste di aiuto agli amministratori (ADR 0032). */
  support: SupportApi;
  /** Agente di sistema per gli allarmi (ADR 0034, solo amministratori). */
  systemAgent: SystemAgentApi;
  /** Uscita su elenco dei siti ospitati (ADR 0039 §3). */
  siteEgress: SiteEgressApi;
  /** Spazio dei volumi dei siti ospitati, pagato con i crediti oltre il piano (ADR 0046). */
  hostingStorage: HostingStorageApi;
  /** Repository GitHub delle release della cache (ADR 0039 §2), per gli amministratori. */
  releaseRepos: ReleaseReposApi;
  /** Pagina dei permessi del set (ADR 0041). */
  permissions: PermissionsApi;
  /** Codici regalo e inviti dei creator (ADR 0043). */
  giftCodes: GiftCodesApi;
  /** Scadenze della piattaforma (ADR 0044). */
  deadlines: DeadlinesApi;
  /** Pubblicazione delle app mobili sugli store (ADR 0045). */
  mobileRelease: MobileReleaseApi;
  /** Scheda tecnica dei progetti e modifiche richieste (ADR 0048). */
  projectProfile: ProjectProfileApi;
  /** Produzione, rilasci e domini dell'utente dei siti ospitati (ADR 0050 §4, §5). */
  hostingEnvironments: HostingEnvironmentsApi;
  /** Link della demo privata (ADR 0050 §3). */
  demoLinks: DemoLinksApi;
  /** Requisiti funzionali dei progetti (ADR 0050 §6). */
  requirements: RequirementsApi;
  /** Analisi degli specialisti della piattaforma, come lo specialista SEO (ADR 0059). */
  specialists: SpecialistsApi;
}

const ApiContext = createContext<ApiBundle | null>(null);

/** Proprietà del provider con client già costruiti. */
export interface ApiProviderProps {
  /** Client da rendere disponibili. */
  value: ApiBundle;
  /** Sottoalbero da servire. */
  children: ReactNode;
}

/**
 * Rende disponibili al sottoalbero client già costruiti.
 *
 * @param props.value Client da esporre.
 * @param props.children Sottoalbero da servire.
 * @returns Il provider di contesto.
 */
export function ApiProvider({ value, children }: ApiProviderProps): JSX.Element {
  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
}

/** Proprietà del provider legato alla sessione. */
export interface SessionApiProviderProps {
  /** Sottoalbero da servire. */
  children: ReactNode;
}

/**
 * Costruisce i client sul client HTTP della sessione e li espone.
 *
 * @param props.children Sottoalbero da servire.
 * @returns Il provider di contesto.
 */
export function SessionApiProvider({ children }: SessionApiProviderProps): JSX.Element {
  const { client } = useAuth();
  const value = useMemo<ApiBundle>(
    () => ({
      projects: new ProjectsApi(client),
      agents: new AgentsApi(client),
      secrets: new SecretsApi(client),
      tools: new ToolsApi(client),
      templates: new TemplatesApi(client),
      alerts: new AlertsApi(client),
      voice: new VoiceApi(client),
      billing: new BillingApi(client),
      priceList: new PriceListApi(client),
      tasks: new TasksApi(client),
      account: new AccountApi(client),
      directory: new DirectoryApi(client),
      team: new TeamApi(client),
      work: new WorkApi(client),
      support: new SupportApi(client),
      systemAgent: new SystemAgentApi(client),
      siteEgress: new SiteEgressApi(client),
      hostingStorage: new HostingStorageApi(client),
      releaseRepos: new ReleaseReposApi(client),
      permissions: new PermissionsApi(client),
      giftCodes: new GiftCodesApi(client),
      deadlines: new DeadlinesApi(client),
      mobileRelease: new MobileReleaseApi(client),
      projectProfile: new ProjectProfileApi(client),
      hostingEnvironments: new HostingEnvironmentsApi(client),
      demoLinks: new DemoLinksApi(client),
      requirements: new RequirementsApi(client),
      specialists: new SpecialistsApi(client),
    }),
    [client],
  );
  return <ApiProvider value={value}>{children}</ApiProvider>;
}

/**
 * Accede ai client delle API.
 *
 * @returns I client di progetti, agenti, credenziali, strumenti, template e allarmi.
 * @throws Error Se usato fuori da un provider.
 */
export function useApi(): ApiBundle {
  const bundle = useContext(ApiContext);
  if (bundle === null) {
    throw new Error('useApi richiede ApiProvider');
  }
  return bundle;
}
